import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Search, ArrowRight, Truck, ShieldCheck, Scale } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TripCard } from "@/components/TripCard";
import { ContactCard } from "@/components/ContactCard";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, setRef] = useState("");

  useEffect(() => {
    api.get("/trips").then(({ data }) => setTrips(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-blue-500/10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 space-y-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
              <Package className="h-4 w-4" /> {t("hero_badge")}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight font-display">
              {t("hero_title")}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" data-testid="hero-book-btn" onClick={() => navigate("/book")}>
                {t("hero_cta_book")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-track-btn" onClick={() => navigate("/track")}>
                {t("hero_cta_track")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 pt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Door-to-door</span>
              <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Reweigh transparan</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Trusted by Zaki & Aini</span>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <Card className="p-6 space-y-4 shadow-xl">
              <div className="flex items-center gap-2 font-display font-semibold text-lg">
                <Search className="h-5 w-5 text-primary" /> {t("nav_track")}
              </div>
              <p className="text-sm text-muted-foreground">{t("track_sub")}</p>
              <div className="flex gap-2">
                <Input data-testid="quick-track-input" placeholder="JST-2026-BHX123" value={ref}
                  onChange={(e) => setRef(e.target.value)} />
                <Button data-testid="quick-track-btn"
                  onClick={() => navigate(`/track${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`)}>
                  {t("track_btn")}
                </Button>
              </div>
              <div className="pt-2"><ContactCard /></div>
            </Card>
          </div>
        </div>
      </section>

      {/* Trips */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">{t("trips_title")}</h2>
          <p className="text-muted-foreground mt-1">{t("trips_sub")}</p>
        </div>
        {loading ? (
          <p className="text-muted-foreground">{t("loading")}</p>
        ) : trips.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground" data-testid="no-trips">{t("no_trips")}</Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trips-grid">
            {trips.map((trip, i) => <TripCard key={trip.id} trip={trip} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
